import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { type SingleFilterProps } from '~/components/Filters/types';
import {
  mapOptionToUploaderFilter,
  mapUploaderFilterToOption,
  UploaderFilter,
} from '~/components/Filters/UploaderFilter/UploaderFilter';
import { clearUploadersFilterValuesAction, setUploadersFilterValuesAction } from '~/store/filters/actions';
import { hasUploadersFilterValuesSelector, selectedUploadersFilterSelector } from '~/store/filters/selectors';
import { type UploaderFilterType } from '~/store/filters/types';
import { useAirSelector } from '~/utils/ReduxUtils';
import { type Uploader } from './UploaderFilterCard';

export type SingleUploaderFilterProps = {
  uploaders: UploaderFilterType[];
} & SingleFilterProps;

export const SingleUploaderFilter = ({ uploaders, onApplyClick }: SingleUploaderFilterProps) => {
  const dispatch = useDispatch();
  const hasValues = useAirSelector(hasUploadersFilterValuesSelector);
  const selectedUploaders = useSelector(selectedUploadersFilterSelector);

  const clearValues = useCallback(() => {
    dispatch(clearUploadersFilterValuesAction());
  }, [dispatch]);

  const uploadersOptions = useMemo(() => uploaders.map((u) => mapUploaderFilterToOption(u)), [uploaders]);

  const selectedOptions = useMemo(
    () => selectedUploaders.map((u) => mapUploaderFilterToOption(u)),
    [selectedUploaders],
  );

  const onUploaderSelectionChange = useCallback(
    (uploaders: Uploader[]) => {
      dispatch(setUploadersFilterValuesAction({ uploaders: uploaders.map((u) => mapOptionToUploaderFilter(u)) }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  return (
    <SingleFilterContainer
      hasValues={hasValues}
      currentFilter="source"
      onClearClick={clearValues}
      onApplyClick={onApplyClick}
    >
      <UploaderFilter
        onUploaderSelectionChange={onUploaderSelectionChange}
        uploadersOptions={uploadersOptions}
        selectedOptions={selectedOptions}
      />
    </SingleFilterContainer>
  );
};
